import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import CostCalculatorTab from './CostCalculatorTab';
import InspectionSection from './InspectionSection';
import { 
  Car, 
  Truck, 
  DollarSign, 
  Calendar, 
  MapPin, 
  Ship, 
  FileText,
  Calculator,
  Package,
  Clock,
  CheckCircle,
  Upload,
  Eye,
  Trash2,
  X
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { extractErrorMessage } from '../../utils/errorHandler';
import DocumentManager from '../documents/DocumentManager';
import PublicListingTab from './PublicListingTab';

import { BACKEND_URL_EXPORT as BACKEND_URL, API } from '../../config';

// Format date to dd/Oct/year
const formatDateToDDMonYear = (dateString) => {
  if (!dateString) return 'Not specified';
  const date = new Date(dateString);
  const day = date.getDate();
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const month = monthNames[date.getMonth()];
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

// Convert plain-text URLs into clickable links
const URL_REGEX = /(https?:\/\/[^\s<>"']+|www\.[^\s<>"']+)/gi;

const linkifyText = (text) => {
  if (!text) return null;
  return text.split(URL_REGEX).map((part, index) => {
    if (!part) return null;
    if (/^(https?:\/\/|www\.)\S+$/i.test(part)) {
      const href = part.startsWith('http') ? part : `https://${part}`;
      return (
        <a
          key={index}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 underline hover:text-blue-800 break-all"
        >
          {part}
        </a>
      );
    }
    return part;
  });
};

const VehicleDetailsDialog = ({ vehicle, isOpen, onClose, onUpdate, user }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [activeTab, setActiveTab] = useState('details');
  
  // Internal Auction Sheet state
  const [uploadingAuctionSheet, setUploadingAuctionSheet] = useState(false);
  const [viewingAuctionSheet, setViewingAuctionSheet] = useState(false);
  const [auctionSheetDataUrl, setAuctionSheetDataUrl] = useState(null);
  const [loadingAuctionSheet, setLoadingAuctionSheet] = useState(false);

  // Condition Report state
  const [uploadingConditionReport, setUploadingConditionReport] = useState(false);
  const [viewingConditionReport, setViewingConditionReport] = useState(false);
  const [conditionReportDataUrl, setConditionReportDataUrl] = useState(null);
  const [loadingConditionReport, setLoadingConditionReport] = useState(false);
  
  // Check if user has permission for an action
  const hasPermission = (action) => {
    // Admin has all permissions
    if (user?.role === 'admin') return true;
    
    // Check employee permissions
    if (user?.role === 'employee' && user?.permissions) {
      return user.permissions[action] === true;
    }
    
    return false;
  };

  const [formData, setFormData] = useState({
    // Basic vehicle information
    year: vehicle?.year || '',
    type: vehicle?.type || '',
    model: vehicle?.model || '',
    stock_no: vehicle?.stock_no || '',
    chassis_no: vehicle?.chassis_no || '',
    auction_house: vehicle?.auction_house || '',
    yard: vehicle?.yard || '',
    purchase_date: vehicle?.purchase_date || '',
    // Status and sales
    status: vehicle?.status || 'purchased',
    estimated_sale_jpy: vehicle?.estimated_sale_jpy || '',
    estimated_sale_usd: vehicle?.estimated_sale_usd || '',
    confirmed_sale_jpy: vehicle?.confirmed_sale_jpy || '',
    confirmed_sale_usd: vehicle?.confirmed_sale_usd || '',
    // Shipping information
    pol: vehicle?.pol || '',
    pod: vehicle?.pod || '',
    forwarder: vehicle?.forwarder || '',
    vessel_name: vehicle?.vessel_name || '',
    vessel_type: vehicle?.vessel_type || '',
    payment_terms: vehicle?.payment_terms || '',
    country: vehicle?.country || '',
    etd: vehicle?.etd || '',
    eta: vehicle?.eta || '',
    tracking_provider: vehicle?.tracking_provider || '',
    tracking_number: vehicle?.tracking_number || '',
    shipping_booking_applied: vehicle?.shipping_booking_applied || '',
    // Costs
    transport_cost_jpy: vehicle?.transport_cost_jpy || '',
    transport_cost_usd: vehicle?.transport_cost_usd || '',
    freight_cost_jpy: vehicle?.freight_cost_jpy || '',
    freight_cost_usd: vehicle?.freight_cost_usd || '',
    inspection_cost_jpy: vehicle?.inspection_cost_jpy || '',
    inspection_cost_usd: vehicle?.inspection_cost_usd || '',
    // Inspection details
    inspection_date: vehicle?.inspection_date || '',
    inspection_status: vehicle?.inspection_status || 'pending',
    inspection_remarks: vehicle?.inspection_remarks || '',
    reinspection_date: vehicle?.reinspection_date || '',
    inspection_repair_charges: vehicle?.inspection_repair_charges || '',
    // Employee assignment
    assigned_employee_id: vehicle?.assigned_employee_id || '',
    // Internal notes
    notes: vehicle?.notes || ''
  });

  // Reset to details tab only when dialog first opens
  useEffect(() => {
    if (isOpen) {
      setActiveTab('details');
    }
  }, [isOpen]);

  // Update form data when vehicle changes
  useEffect(() => {
    if (isOpen && vehicle) {
      fetchEmployees();
      // Reset form data with vehicle data
      setFormData({
        year: vehicle?.year || '',
        type: vehicle?.type || '',
        model: vehicle?.model || '',
        stock_no: vehicle?.stock_no || '',
        chassis_no: vehicle?.chassis_no || '',
        auction_house: vehicle?.auction_house || '',
        yard: vehicle?.yard || '',
        purchase_date: vehicle?.purchase_date || '',
        status: vehicle?.status || 'purchased',
        estimated_sale_jpy: vehicle?.estimated_sale_jpy || '',
        estimated_sale_usd: vehicle?.estimated_sale_usd || '',
        confirmed_sale_jpy: vehicle?.confirmed_sale_jpy || '',
        confirmed_sale_usd: vehicle?.confirmed_sale_usd || '',
        pol: vehicle?.pol || '',
        pod: vehicle?.pod || '',
        forwarder: vehicle?.forwarder || '',
        vessel_name: vehicle?.vessel_name || '',
        vessel_type: vehicle?.vessel_type || '',
        payment_terms: vehicle?.payment_terms || '',
        country: vehicle?.country || '',
        etd: vehicle?.etd || '',
        eta: vehicle?.eta || '',
        tracking_provider: vehicle?.tracking_provider || '',
        tracking_number: vehicle?.tracking_number || '',
        shipping_booking_applied: vehicle?.shipping_booking_applied || '',
        transport_cost_jpy: vehicle?.transport_cost_jpy || '',
        transport_cost_usd: vehicle?.transport_cost_usd || '',
        freight_cost_jpy: vehicle?.freight_cost_jpy || '',
        freight_cost_usd: vehicle?.freight_cost_usd || '',
        inspection_cost_jpy: vehicle?.inspection_cost_jpy || '',
        inspection_date: vehicle?.inspection_date || '',
        inspection_status: vehicle?.inspection_status || 'pending',
        inspection_remarks: vehicle?.inspection_remarks || '',
        reinspection_date: vehicle?.reinspection_date || '',
        inspection_repair_charges: vehicle?.inspection_repair_charges || '',
        inspection_cost_usd: vehicle?.inspection_cost_usd || '',
        assigned_employee_id: vehicle?.assigned_employee_id || '',
        notes: vehicle?.notes || ''
      });
      // Reset editing state when vehicle data refreshes
      setIsEditing(false);
    }
  }, [vehicle]);

  const fetchEmployees = async () => {
    setLoadingEmployees(true);
    try {
      const response = await axios.get(`${API}/employees`);
      setEmployees(response.data);
      console.log('Fetched employees:', response.data);
    } catch (error) {
      console.error('Error fetching employees:', error);
      toast.error('Failed to load employees list');
    } finally {
      setLoadingEmployees(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const updateData = {};
      
      // List of numeric fields
      const numericFields = ['year', 'estimated_sale_jpy', 'estimated_sale_usd', 'confirmed_sale_jpy', 
                            'confirmed_sale_usd', 'transport_cost_jpy', 'transport_cost_usd', 
                            'freight_cost_jpy', 'freight_cost_usd', 'inspection_cost_jpy', 'inspection_cost_usd'];
      
      // Only include changed fields
      Object.keys(formData).forEach(key => {
        const formValue = formData[key];
        const vehicleValue = vehicle[key];
        
        // Skip if form value is empty string and vehicle value is null/undefined
        if (formValue === '' && (vehicleValue === null || vehicleValue === undefined)) {
          return;
        }
        
        // Handle numeric fields
        if (numericFields.includes(key)) {
          const numValue = formValue !== '' ? parseFloat(formValue) : null;
          const currentValue = vehicleValue !== null && vehicleValue !== undefined ? vehicleValue : null;
          if (numValue !== currentValue) {
            updateData[key] = numValue !== null ? numValue : 0;
          }
        } else {
          // Handle string fields
          const stringValue = formValue !== '' ? formValue : null;
          const currentValue = vehicleValue || null;
          if (stringValue !== currentValue) {
            updateData[key] = stringValue;
          }
        }
      });

      console.log('Update data being sent:', updateData); // Debug log

      if (Object.keys(updateData).length > 0) {
        const response = await axios.put(`${API}/vehicles/${vehicle.id}`, updateData);
        console.log('Update response:', response.data); // Debug log
        toast.success('Vehicle updated successfully!');
        await onUpdate(); // Ensure onUpdate completes
        setIsEditing(false);
      } else {
        toast.info('No changes to save');
        setIsEditing(false);
      }
    } catch (error) {
      console.error('Error updating vehicle:', error);
      console.error('Error response:', error.response?.data); // Debug log
      const message = extractErrorMessage(error, 'Failed to update vehicle');
      toast.error(message);
    } finally {
      setIsSaving(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'purchased':
        return 'bg-yellow-100 text-yellow-800';
      case 'shipped':
        return 'bg-blue-100 text-blue-800';
      case 'delivered':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'purchased':
        return <Package className="h-4 w-4" />;
      case 'shipped':
        return <Clock className="h-4 w-4" />;
      case 'delivered':
        return <CheckCircle className="h-4 w-4" />;
      default:
        return <Package className="h-4 w-4" />;
    }
  };

  const calculateTotalCosts = () => {
    const costs = [
      vehicle?.purchase_cost_jpy || 0,
      vehicle?.transport_cost_jpy || 0,
      vehicle?.freight_cost_jpy || 0,
      vehicle?.inspection_cost_jpy || 0
    ];
    return costs.reduce((sum, cost) => sum + cost, 0);
  };

  // Internal Auction Sheet handlers
  const handleAuctionSheetUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingAuctionSheet(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      await axios.post(`${API}/vehicles/${vehicle.id}/internal-auction-sheet`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success('Internal auction sheet uploaded successfully!');
      onUpdate();
      e.target.value = '';
    } catch (error) {
      console.error('Error uploading auction sheet:', error);
      toast.error('Failed to upload auction sheet');
    } finally {
      setUploadingAuctionSheet(false);
    }
  };

  const handleAuctionSheetDelete = async () => {
    if (!window.confirm('Are you sure you want to delete the internal auction sheet?')) {
      return;
    }

    try {
      await axios.delete(`${API}/vehicles/${vehicle.id}/internal-auction-sheet`);
      toast.success('Internal auction sheet deleted successfully!');
      onUpdate();
    } catch (error) {
      console.error('Error deleting auction sheet:', error);
      toast.error('Failed to delete auction sheet');
    }
  };

  const loadAuctionSheetWithAuth = async () => {
    if (!vehicle?.internal_auction_sheet) return;

    setLoadingAuctionSheet(true);
    try {
      // Backend proxies the file - stable URL that never expires
      const backendUrl = `${API}/internal-auction-sheets/${vehicle.id}`;
      
      // Fetch the file through backend proxy
      const response = await axios.get(backendUrl, {
        responseType: 'blob'  // Get file as blob
      });
      
      // Convert blob to data URL for display
      const reader = new FileReader();
      reader.onloadend = () => {
        setAuctionSheetDataUrl(reader.result);
        setLoadingAuctionSheet(false);
      };
      reader.onerror = () => {
        throw new Error('Failed to read file');
      };
      reader.readAsDataURL(response.data);
    } catch (error) {
      console.error('Error loading auction sheet:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to load auction sheet';
      toast.error(errorMsg);
      setLoadingAuctionSheet(false);
      setViewingAuctionSheet(false);
    }
  };

  // Load auction sheet when viewer opens
  useEffect(() => {
    if (viewingAuctionSheet && vehicle?.internal_auction_sheet) {
      loadAuctionSheetWithAuth();
    } else {
      setAuctionSheetDataUrl(null);
    }
  }, [viewingAuctionSheet, vehicle?.internal_auction_sheet]);

  const handleConditionReportUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploadingConditionReport(true);
    try {
      const uploadData = new FormData();
      uploadData.append('file', file);

      await axios.post(`${API}/vehicles/${vehicle.id}/condition-report`, uploadData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

        toast.success('Conditional report uploaded successfully!');
      onUpdate();
    } catch (error) {
      console.error('Error uploading condition report:', error);
        toast.error('Failed to upload conditional report');
    } finally {
      setUploadingConditionReport(false);
      event.target.value = '';
    }
  };

  const handleConditionReportDelete = async () => {
      if (!window.confirm('Are you sure you want to delete the conditional report?')) {
      return;
    }

    try {
      await axios.delete(`${API}/vehicles/${vehicle.id}/condition-report`);
        toast.success('Conditional report deleted successfully!');
      onUpdate();
    } catch (error) {
      console.error('Error deleting condition report:', error);
        toast.error('Failed to delete conditional report');
    }
  };

  const loadConditionReportWithAuth = async () => {
    if (!vehicle?.condition_report) return;

    setLoadingConditionReport(true);
    try {
      const backendUrl = `${API}/condition-reports/${vehicle.id}`;

      const response = await axios.get(backendUrl, {
        responseType: 'blob'
      });

      const reader = new FileReader();
      reader.onloadend = () => {
        setConditionReportDataUrl(reader.result);
        setLoadingConditionReport(false);
      };
      reader.onerror = () => {
        throw new Error('Failed to read file');
      };
      reader.readAsDataURL(response.data);
    } catch (error) {
      console.error('Error loading condition report:', error);
        const errorMsg = error.response?.data?.detail || 'Failed to load conditional report';
      toast.error(errorMsg);
      setLoadingConditionReport(false);
      setViewingConditionReport(false);
    }
  };

  // Load condition report when viewer opens
  useEffect(() => {
    if (viewingConditionReport && vehicle?.condition_report) {
      loadConditionReportWithAuth();
    } else {
      setConditionReportDataUrl(null);
    }
  }, [viewingConditionReport, vehicle?.condition_report]);

  const calculateProfit = () => {
    const totalCosts = calculateTotalCosts();
    const revenue = vehicle?.confirmed_sale_jpy || vehicle?.estimated_sale_jpy || 0;
    return revenue - totalCosts;
  };

  if (!vehicle) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-3">
            <Car className="h-6 w-6 text-blue-600" />
            <span>{vehicle.year} {vehicle.model}</span>
            <Badge className={`${getStatusColor(vehicle.status)} flex items-center space-x-1`}>
              {getStatusIcon(vehicle.status)}
              <span className="capitalize">{vehicle.status}</span>
            </Badge>
          </DialogTitle>
          <DialogDescription>
            Stock No: {vehicle.stock_no} | Chassis: {vehicle.chassis_no}
          </DialogDescription>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="details">Vehicle Details</TabsTrigger>
            <TabsTrigger value="listing">Public Listing</TabsTrigger>
            <TabsTrigger value="shipping">Shipping Info</TabsTrigger>
            <TabsTrigger value="costs">Cost Calculator</TabsTrigger>
            <TabsTrigger value="documents">Documents</TabsTrigger>
          </TabsList>

          <TabsContent value="details" className="space-y-4">
            <div className="grid grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center space-x-2">
                    <Car className="h-5 w-5" />
                    <span>Vehicle Information</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {isEditing ? (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="type">Type</Label>
                        <Select 
                          value={formData.type} 
                          onValueChange={(value) => setFormData({...formData, type: value})}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="sedan">Sedan</SelectItem>
                            <SelectItem value="suv">SUV</SelectItem>
                            <SelectItem value="truck">Truck</SelectItem>
                            <SelectItem value="van">Van</SelectItem>
                            <SelectItem value="coupe">Coupe</SelectItem>
                            <SelectItem value="station wagon">Station Wagon</SelectItem>
                            <SelectItem value="hatchback">Hatchback</SelectItem>
                            <SelectItem value="bus">Bus</SelectItem>
                            <SelectItem value="other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor="model">Model</Label>
                        <Input
                          id="model"
                          name="model"
                          value={formData.model}
                          onChange={handleInputChange}
                          placeholder="Toyota Corolla"
                        />
                      </div>
                      <div>
                        <Label htmlFor="stock_no">Stock No</Label>
                        <Input
                          id="stock_no"
                          name="stock_no"
                          value={formData.stock_no}
                          onChange={handleInputChange}
                          placeholder="STK-12345"
                        />
                      </div>
                      <div>
                        <Label htmlFor="chassis_no">Chassis Number</Label>
                        <Input
                          id="chassis_no"
                          name="chassis_no"
                          value={formData.chassis_no}
                          onChange={handleInputChange}
                          placeholder="JT123456789"
                          className="font-mono text-xs"
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="font-medium text-slate-600">Type</p>
                        <p className="text-slate-900 capitalize">{vehicle.type}</p>
                      </div>
                      <div>
                        <p className="font-medium text-slate-600">Model</p>
                        <p className="text-slate-900">{vehicle.model}</p>
                      </div>
                      <div>
                        <p className="font-medium text-slate-600">Stock No</p>
                        <p className="text-slate-900">{vehicle.stock_no}</p>
                      </div>
                      <div>
                        <p className="font-medium text-slate-600">Chassis Number</p>
                        <p className="text-slate-900 font-mono text-xs">{vehicle.chassis_no}</p>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center space-x-2">
                    <MapPin className="h-5 w-5" />
                    <span>Auction Information</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {isEditing ? (
                    <div className="grid grid-cols-1 gap-4">
                      <div>
                        <Label htmlFor="auction_house">Auction House</Label>
                        <Input
                          id="auction_house"
                          name="auction_house"
                          value={formData.auction_house}
                          onChange={handleInputChange}
                          placeholder="USS Tokyo, TAA, etc."
                        />
                      </div>
                      <div>
                        <Label htmlFor="yard">Yard</Label>
                        <Input
                          id="yard"
                          name="yard"
                          value={formData.yard}
                          onChange={handleInputChange}
                          placeholder="Yard location"
                        />
                      </div>
                      <div>
                        <Label htmlFor="entry_date">Entry Date (Auto-set)</Label>
                        <Input
                          id="entry_date"
                          name="entry_date"
                          type="text"
                          value={vehicle.entry_date ? new Date(vehicle.entry_date).toLocaleDateString() : 'N/A'}
                          disabled
                          className="bg-slate-50 cursor-not-allowed"
                        />
                        <p className="text-xs text-slate-500 mt-1">
                          Automatically set when vehicle was entered into the system
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 gap-4 text-sm">
                      <div>
                        <p className="font-medium text-slate-600">Auction House</p>
                        <p className="text-slate-900">{vehicle.auction_house}</p>
                      </div>
                      <div>
                        <p className="font-medium text-slate-600">Yard</p>
                        <p className="text-slate-900">{vehicle.yard}</p>
                      </div>
                      <div>
                        <p className="font-medium text-slate-600">Entry Date</p>
                        <p className="text-slate-900">{vehicle.entry_date ? new Date(vehicle.entry_date).toLocaleDateString() : 'N/A'}</p>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

              {/* Inspection Section - Admin/Employee Only */}
            {user?.role !== 'customer' && (
              <InspectionSection 
                vehicle={vehicle}
                formData={formData}
                setFormData={setFormData}
                isEditing={isEditing}
              />
            )}

            {/* Internal Auction Sheet - Admin/Employee Only */}
            {user?.role !== 'customer' && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center space-x-2">
                    <FileText className="h-5 w-5" />
                    <span>Internal Auction Sheet</span>
                    <Badge variant="outline" className="ml-2">Admin/Employee Only</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {vehicle?.internal_auction_sheet ? (
                    <div className="space-y-4">
                      <div className="border-2 border-dashed rounded-lg p-6 text-center">
                        <FileText className="h-16 w-16 text-blue-500 mx-auto mb-4" />
                        <p className="text-sm text-slate-600 mb-4">Auction sheet uploaded</p>
                        <div className="flex justify-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setViewingAuctionSheet(true)}
                          >
                            <Eye className="h-4 w-4 mr-2" />
                            View Sheet
                          </Button>
                          {hasPermission('vehicles_edit') && (
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={handleAuctionSheetDelete}
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="border-2 border-dashed rounded-lg p-6 text-center">
                      <FileText className="h-16 w-16 text-slate-300 mx-auto mb-4" />
                      <p className="text-slate-500 mb-4">No auction sheet uploaded</p>
                      {hasPermission('vehicles_edit') && (
                        <Label htmlFor="auction-sheet-upload">
                          <Button variant="outline" size="sm" asChild disabled={uploadingAuctionSheet}>
                            <span>
                              {uploadingAuctionSheet ? (
                                <>
                                  <Clock className="h-4 w-4 mr-2 animate-spin" />
                                  Uploading...
                                </>
                              ) : (
                                <>
                                  <Upload className="h-4 w-4 mr-2" />
                                  Upload Auction Sheet
                                </>
                              )}
                            </span>
                          </Button>
                          <Input
                            id="auction-sheet-upload"
                            type="file"
                            accept="image/*,application/pdf"
                            onChange={handleAuctionSheetUpload}
                            className="hidden"
                          />
                        </Label>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Condition Report - Admin/Employee Only */}
            {user?.role !== 'customer' && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center space-x-2">
                    <FileText className="h-5 w-5" />
                    <span>Conditional Report</span>
                    <Badge variant="outline" className="ml-2">Admin/Employee Only</Badge>
                  </CardTitle>
                  <CardDescription>Conditional report is only visible to staff members</CardDescription>
                </CardHeader>
                <CardContent>
                  {vehicle?.condition_report ? (
                    <div className="space-y-4">
                      <div className="border-2 border-dashed rounded-lg p-6 text-center">
                        <FileText className="h-16 w-16 text-blue-500 mx-auto mb-4" />
                        <p className="text-sm text-slate-600 mb-4">Conditional report uploaded</p>
                        <div className="flex justify-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setViewingConditionReport(true)}
                          >
                            <Eye className="h-4 w-4 mr-2" />
                            View Report
                          </Button>
                          {hasPermission('vehicles_edit') && (
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={handleConditionReportDelete}
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="border-2 border-dashed rounded-lg p-6 text-center">
                      <FileText className="h-16 w-16 text-slate-300 mx-auto mb-4" />
                        <p className="text-slate-500 mb-4">No conditional report uploaded</p>
                      {hasPermission('vehicles_edit') && (
                        <Label htmlFor="condition-report-upload">
                          <Button variant="outline" size="sm" asChild disabled={uploadingConditionReport}>
                            <span>
                              {uploadingConditionReport ? (
                                <>
                                  <Clock className="h-4 w-4 mr-2 animate-spin" />
                                  Uploading...
                                </>
                              ) : (
                                <>
                                  <Upload className="h-4 w-4 mr-2" />
                                  Upload Conditional Report
                                </>
                              )}
                            </span>
                          </Button>
                          <Input
                            id="condition-report-upload"
                            type="file"
                            accept="image/*,application/pdf"
                            onChange={handleConditionReportUpload}
                            className="hidden"
                          />
                        </Label>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Internal Notes - Below Internal Auction Sheet */}
            {user?.role !== 'customer' && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center space-x-2">
                    <FileText className="h-5 w-5" />
                    <span>Internal Notes</span>
                  </CardTitle>
                  <CardDescription>Notes are only visible to staff members</CardDescription>
                </CardHeader>
                <CardContent>
                  {isEditing ? (
                    <div>
                      <Label htmlFor="notes">Notes</Label>
                      <textarea
                        id="notes"
                        name="notes"
                        value={formData.notes}
                        onChange={handleInputChange}
                        placeholder="Add internal notes here..."
                        className="w-full min-h-[200px] p-3 border rounded-md mt-2"
                      />
                    </div>
                  ) : (
                    <div className="min-h-[100px]">
                      {vehicle?.notes ? (
                        <p className="text-slate-900 whitespace-pre-wrap">{linkifyText(vehicle.notes)}</p>
                      ) : (
                        <p className="text-slate-400 italic">No notes added yet</p>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {user?.role !== 'customer' && hasPermission('vehicles_edit') && (
              <div className="flex justify-end space-x-3">
                {!isEditing ? (
                  <Button onClick={() => setIsEditing(true)} disabled={isSaving}>
                    Edit Details
                  </Button>
                ) : (
                  <>
                    <Button variant="outline" onClick={() => setIsEditing(false)} disabled={isSaving}>
                      Cancel
                    </Button>
                    <Button onClick={handleSave} disabled={isSaving}>
                      {isSaving ? 'Saving...' : 'Save Changes'}
                    </Button>
                  </>
                )}
              </div>
            )}
          </TabsContent>


          <TabsContent value="listing" className="space-y-4">
            <PublicListingTab 
              vehicle={vehicle}
              onUpdate={onUpdate}
              isEditing={isEditing}
              setIsEditing={setIsEditing}
              canEdit={hasPermission('vehicles_edit')}
            />
          </TabsContent>

          <TabsContent value="shipping" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center space-x-2">
                  <Ship className="h-5 w-5" />
                  <span>Shipping Information</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {isEditing ? (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="pol">Port of Loading (POL)</Label>
                      <Input
                        id="pol"
                        name="pol"
                        value={formData.pol}
                        onChange={handleInputChange}
                        placeholder="Tokyo, Yokohama, etc."
                      />
                    </div>
                    <div>
                      <Label htmlFor="pod">Port of Discharge (POD)</Label>
                      <Input
                        id="pod"
                        name="pod"
                        value={formData.pod}
                        onChange={handleInputChange}
                        placeholder="Destination port"
                      />
                    </div>
                    <div>
                      <Label htmlFor="country">Country</Label>
                      <Input
                        id="country"
                        name="country"
                        value={formData.country}
                        onChange={handleInputChange}
                        placeholder="Destination country"
                      />
                    </div>
                    <div>
                      <Label htmlFor="forwarder">Forwarder</Label>
                      <Input
                        id="forwarder"
                        name="forwarder"
                        value={formData.forwarder}
                        onChange={handleInputChange}
                        placeholder="Shipping company"
                      />
                    </div>
                    <div>
                      <Label htmlFor="tracking_provider">Tracking Provider</Label>
                      <Select
                        value={formData.tracking_provider}
                        onValueChange={(value) => {
                          setFormData(prev => ({
                            ...prev,
                            tracking_provider: value,
                            tracking_number: value ? prev.tracking_number : ''
                          }));
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select provider" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="DHL">DHL</SelectItem>
                          <SelectItem value="FEDEX">FEDEX</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {formData.tracking_provider && (
                      <div>
                        <Label htmlFor="tracking_number">Tracking Number</Label>
                        <Input
                          id="tracking_number"
                          name="tracking_number"
                          value={formData.tracking_number}
                          onChange={handleInputChange}
                          placeholder="Enter tracking number"
                        />
                      </div>
                    )}
                    <div>
                      <Label htmlFor="vessel_name">Vessel Name</Label>
                      <Input
                        id="vessel_name"
                        name="vessel_name"
                        value={formData.vessel_name}
                        onChange={handleInputChange}
                        placeholder="Ship name"
                      />
                    </div>
                    <div>
                      <Label htmlFor="vessel_type">Shipment Method</Label>
                      <Select 
                        value={formData.vessel_type} 
                        onValueChange={(value) => setFormData({...formData, vessel_type: value})}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select shipment method" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="RoRo">RoRo</SelectItem>
                          <SelectItem value="Container">Container</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {formData.vessel_type === 'Container' && (
                      <div>
                        <Label htmlFor="payment_terms">Payment Terms</Label>
                        <Select 
                          value={formData.payment_terms} 
                          onValueChange={(value) => setFormData({...formData, payment_terms: value})}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select payment terms" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Prepaid">Prepaid</SelectItem>
                            <SelectItem value="Collect">Collect</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    <div>
                      <Label htmlFor="shipping_booking_applied">Shipping Booking Applied</Label>
                      <Select
                        value={formData.shipping_booking_applied || ''}
                        onValueChange={(value) => setFormData({...formData, shipping_booking_applied: value})}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select shipping booking status" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="yes">Yes - Booking Applied</SelectItem>
                          <SelectItem value="pending">Pending</SelectItem>
                          <SelectItem value="no">No</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="etd">ETD (Estimated Time of Departure)</Label>
                      <Input
                        id="etd"
                        name="etd"
                        type="date"
                        value={formData.etd}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div>
                      <Label htmlFor="eta">ETA (Estimated Time of Arrival)</Label>
                      <Input
                        id="eta"
                        name="eta"
                        type="date"
                        value={formData.eta}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div>
                      <Label htmlFor="release_date">Release Date</Label>
                      <Input
                        id="release_date"
                        name="release_date"
                        type="date"
                        value={formData.release_date}
                        onChange={handleInputChange}
                      />
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-6 text-sm">
                    <div>
                      <p className="font-medium text-slate-600">Port of Loading</p>
                      <p className="text-slate-900">{vehicle.pol || 'Not specified'}</p>
                    </div>
                    <div>
                      <p className="font-medium text-slate-600">Port of Discharge</p>
                      <p className="text-slate-900">{vehicle.pod || 'Not specified'}</p>
                    </div>
                    <div>
                      <p className="font-medium text-slate-600">Country</p>
                      <p className="text-slate-900">{vehicle.country || 'Not specified'}</p>
                    </div>
                    <div>
                      <p className="font-medium text-slate-600">Forwarder</p>
                      <p className="text-slate-900">{vehicle.forwarder || 'Not specified'}</p>
                    </div>
                    {vehicle.tracking_number && (
                      <div>
                        <p className="font-medium text-slate-600">Tracking</p>
                        <p className="text-slate-900">
                          {(vehicle.tracking_provider || 'Carrier')}: {vehicle.tracking_number}
                        </p>
                      </div>
                    )}
                    <div>
                      <p className="font-medium text-slate-600">Vessel Name</p>
                      <p className="text-slate-900">{vehicle.vessel_name || 'Not specified'}</p>
                    </div>
                    <div>
                      <p className="font-medium text-slate-600">Shipment Method</p>
                      <p className="text-slate-900">{vehicle.vessel_type || 'Not specified'}</p>
                    </div>
                    <div>
                      <p className="font-medium text-slate-600">Shipping Booking Applied</p>
                      <p className="text-slate-900">{vehicle.shipping_booking_applied ? (vehicle.shipping_booking_applied === 'yes' ? 'Yes - Applied' : vehicle.shipping_booking_applied.charAt(0).toUpperCase() + vehicle.shipping_booking_applied.slice(1)) : 'Not specified'}</p>
                    </div>
                    {vehicle.vessel_type === 'Container' && (
                      <div>
                        <p className="font-medium text-slate-600">Payment Terms</p>
                        <p className="text-slate-900">{vehicle.payment_terms || 'Not specified'}</p>
                      </div>
                    )}
                    <div>
                      <p className="font-medium text-slate-600">ETD</p>
                      <p className="text-slate-900">{formatDateToDDMonYear(vehicle.etd)}</p>
                    </div>
                    <div>
                      <p className="font-medium text-slate-600">ETA</p>
                      <p className="text-slate-900">{formatDateToDDMonYear(vehicle.eta)}</p>
                    </div>
                    <div>
                      <p className="font-medium text-slate-600">Release Date</p>
                      <p className="text-slate-900">{formatDateToDDMonYear(vehicle.release_date)}</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
            
            {user?.role !== 'customer' && hasPermission('vehicles_edit') && (
              <div className="flex justify-end space-x-3">
                {!isEditing ? (
                  <Button onClick={() => setIsEditing(true)} disabled={isSaving}>
                    Edit Shipping Info
                  </Button>
                ) : (
                  <>
                    <Button variant="outline" onClick={() => setIsEditing(false)} disabled={isSaving}>
                      Cancel
                    </Button>
                    <Button onClick={handleSave} disabled={isSaving}>
                      {isSaving ? 'Saving...' : 'Save Changes'}
                    </Button>
                  </>
                )}
              </div>
            )}
          </TabsContent>

          <TabsContent value="costs" className="space-y-4">
            <CostCalculatorTab vehicle={vehicle} onUpdate={onUpdate} />
          </TabsContent>

          <TabsContent value="documents" className="space-y-4">
            <DocumentManager 
              entityType="vehicle" 
              entityId={vehicle?.id} 
              user={user}
            />
          </TabsContent>
        </Tabs>
      </DialogContent>
      
      {/* Auction Sheet Viewer Modal */}
      {viewingAuctionSheet && vehicle?.internal_auction_sheet && (
        <Dialog open={viewingAuctionSheet} onOpenChange={setViewingAuctionSheet}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-auto">
            <DialogHeader>
              <div className="flex items-center justify-between">
                <DialogTitle>Internal Auction Sheet</DialogTitle>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setViewingAuctionSheet(false)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </DialogHeader>
            <div className="mt-4">
              {loadingAuctionSheet ? (
                <div className="flex items-center justify-center h-96">
                  <div className="text-center">
                    <Clock className="h-12 w-12 animate-spin text-blue-500 mx-auto mb-4" />
                    <p className="text-slate-600">Loading auction sheet...</p>
                  </div>
                </div>
              ) : auctionSheetDataUrl ? (
                vehicle.internal_auction_sheet.toLowerCase().endsWith('.pdf') ? (
                  <iframe
                    src={auctionSheetDataUrl}
                    type="application/pdf"
                    width="100%"
                    height="600px"
                    className="rounded-lg border"
                    title="Auction Sheet PDF"
                  />
                ) : (
                  <img
                    src={auctionSheetDataUrl}
                    alt="Internal Auction Sheet"
                    className="w-full h-auto rounded-lg border"
                    onClick={() => {
                      const newWindow = window.open();
                      newWindow.document.write(`<img src="${auctionSheetDataUrl}" style="max-width:100%"/>`);
                    }}
                    style={{ cursor: 'pointer' }}
                  />
                )
              ) : (
                <div className="flex items-center justify-center h-96">
                  <p className="text-red-600">Failed to load auction sheet</p>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Condition Report Viewer Modal */}
      {viewingConditionReport && vehicle?.condition_report && (
        <Dialog open={viewingConditionReport} onOpenChange={setViewingConditionReport}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-auto">
            <DialogHeader>
              <div className="flex items-center justify-between">
                <DialogTitle>Conditional Report</DialogTitle>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setViewingConditionReport(false)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </DialogHeader>
            <div className="mt-4">
              {loadingConditionReport ? (
                <div className="flex items-center justify-center h-96">
                  <div className="text-center">
                    <Clock className="h-12 w-12 animate-spin text-blue-500 mx-auto mb-4" />
                    <p className="text-slate-600">Loading conditional report...</p>
                  </div>
                </div>
              ) : conditionReportDataUrl ? (
                vehicle.condition_report.toLowerCase().endsWith('.pdf') ? (
                  <iframe
                    src={conditionReportDataUrl}
                    type="application/pdf"
                    width="100%"
                    height="600px"
                    className="rounded-lg border"
                    title="Conditional Report PDF"
                  />
                ) : (
                  <img
                    src={conditionReportDataUrl}
                    alt="Conditional Report"
                    className="w-full h-auto rounded-lg border"
                    onClick={() => {
                      const newWindow = window.open();
                      newWindow.document.write(`<img src="${conditionReportDataUrl}" style="max-width:100%"/>`);
                    }}
                    style={{ cursor: 'pointer' }}
                  />
                )
              ) : (
                <div className="flex items-center justify-center h-96">
                  <p className="text-red-600">Failed to load conditional report</p>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </Dialog>
  );
};

export default VehicleDetailsDialog;
